// app-pages/AdminPage/OptionCategoryPanel.tsx
"use client";
import {
  POST_CREATE_OPTION_CATEGORY,
  POST_UPDATE_OPTION_CATEGORY,
  DELETE_OPTION_CATEGORY,
} from "api";

import {
  Flex,
  Text,
  Box,
  useDisclosure,
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Editable,
  EditableInput,
  EditablePreview,
  Input,
  VStack,
  useToast,
  Checkbox,
} from "@chakra-ui/react";

import React, { useState, useEffect } from "react";
import { fetch } from "libs";
import { useQueryClient } from "@tanstack/react-query";
import { useGetAllInternalOptionCategories } from "queries";
import { InternalOptionCategory } from "types";

export const OptionCategoryPanel: React.FC = () => {
  const queryClient = useQueryClient();
  const toast = useToast();

  const { data: internalOptionCategories, isLoading: isInternalOptionCategoriesLoading } =
    useGetAllInternalOptionCategories();

  const [selectedInternalOptionCategory, setSelectedInternalOptionCategory] =
    useState<InternalOptionCategory | null>(null);

  const [createOptionCategoryName, setCreateOptionCategoryName] = useState<string>("");
  const [createOptionCategoryOrder, setCreateOptionCategoryOrder] = useState<string>("");
  const [createOptionCategoryIsQuickMode, setCreateOptionCategoryIsQuickMode] =
    useState<boolean>(false);
  const [updateOptionCategoryOrder, setUpdateOptionCategoryOrder] = useState<string>("");
  const [updateOptionCategoryIsQuickMode, setUpdateOptionCategoryIsQuickMode] =
    useState<boolean>(false);

  const {
    isOpen: internalOptionCategoryIsOpen,
    onOpen: internalOptionCategoryOnOpen,
    onClose: internalOptionCategoryOnClose,
  } = useDisclosure();

  useEffect(() => {
    if (internalOptionCategories) {
      setSelectedInternalOptionCategory(null);
    }
  }, [internalOptionCategories]);

  useEffect(() => {
    if (selectedInternalOptionCategory) {
      setUpdateOptionCategoryOrder(String(selectedInternalOptionCategory.order));
      setUpdateOptionCategoryIsQuickMode(selectedInternalOptionCategory.quick_mode);
    }
  }, [selectedInternalOptionCategory]);

  const handleInternalOptionCategoryClick = (internalOptionCategory: InternalOptionCategory) => {
    setSelectedInternalOptionCategory(internalOptionCategory);
    internalOptionCategoryOnOpen();
  };

  const handleInternalOptionCategoryCreate = async () => {
    try {
      if (!createOptionCategoryName || !createOptionCategoryOrder) return;
      const order = Number(createOptionCategoryOrder);

      if (Number.isNaN(order)) {
        toast({
          title: "Invalid order",
          description: "Please enter number for order",
          status: "warning",
          duration: 5000,
          isClosable: true,
        });

        return;
      }

      const optionCategoryData = {
        name: createOptionCategoryName,
        order: order,
        quick_mode: createOptionCategoryIsQuickMode,
      };

      const response = await fetch(POST_CREATE_OPTION_CATEGORY, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(optionCategoryData),
      });

      if (Object.keys(response).length === 0) {
        throw new Error(`Network response was not ok`);
      }

      queryClient.invalidateQueries({
        queryKey: ["internalOptionCategories"],
      });

      queryClient.invalidateQueries({
        queryKey: ["internalStyles"],
      });

      console.log("Option category created successfully", response);

      toast({
        title: "Success",
        description: (response as any).message,
        status: "success",
        duration: 5000,
        isClosable: true,
      });

      setCreateOptionCategoryName("");
      setCreateOptionCategoryOrder("");
      setCreateOptionCategoryIsQuickMode(false);
    } catch (error) {
      console.error("Error creating option category:", error);

      toast({
        title: "Error",
        description: String(
          (error as any).detail
            ? typeof (error as any).detail === "object"
              ? JSON.stringify((error as any).detail)
              : (error as any).detail
            : error
        ),
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleInternalOptionCategoryUpdate = async () => {
    try {
      if (!selectedInternalOptionCategory) return;
      const order = Number(updateOptionCategoryOrder);

      if (Number.isNaN(order)) {
        toast({
          title: "Invalid order",
          description: "Please enter number for order",
          status: "warning",
          duration: 5000,
          isClosable: true,
        });

        return;
      }

      const optionCategoryData = {
        order: order,
        quick_mode: updateOptionCategoryIsQuickMode,
      };

      const response = await fetch(POST_UPDATE_OPTION_CATEGORY(selectedInternalOptionCategory.id), {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(optionCategoryData),
      });

      if (Object.keys(response).length === 0) {
        throw new Error(`Network response was not ok`);
      }

      queryClient.invalidateQueries({
        queryKey: ["internalOptionCategories"],
      });

      queryClient.invalidateQueries({
        queryKey: ["internalStyles"],
      });

      console.log("Option category updated successfully", response);

      toast({
        title: "Success",
        description: (response as any).message,
        status: "success",
        duration: 5000,
        isClosable: true,
      });
    } catch (error) {
      console.error("Error updating option category:", error);

      toast({
        title: "Error",
        description: String(
          (error as any).detail
            ? typeof (error as any).detail === "object"
              ? JSON.stringify((error as any).detail)
              : (error as any).detail
            : error
        ),
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleInternalOptionCategoryDelete = async (force_delete: boolean = false) => {
    try {
      if (!selectedInternalOptionCategory) return;

      const response = await fetch(
        `${DELETE_OPTION_CATEGORY(selectedInternalOptionCategory.id)}?force_delete=${force_delete}`,
        {
          method: "DELETE",
        }
      );

      if (Object.keys(response).length === 0) {
        throw new Error(`Network response was not ok`);
      }

      queryClient.invalidateQueries({
        queryKey: ["internalOptionCategories"],
      });

      queryClient.invalidateQueries({
        queryKey: ["internalStyles"],
      });

      console.log("Option category deleted successfully", response);

      toast({
        title: "Success",
        description: (response as any).message,
        status: "success",
        duration: 5000,
        isClosable: true,
      });
    } catch (error) {
      console.error("Error deleting option category:", error);

      toast({
        title: "Error",
        description: String(
          (error as any).detail
            ? typeof (error as any).detail === "object"
              ? JSON.stringify((error as any).detail)
              : (error as any).detail
            : error
        ),
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  return (
    <Flex w="100%" direction="column" alignItems="center">
      <Text fontWeight="semibold" fontSize="24px" mt="20px">
        Option Category Panel
      </Text>
      {!isInternalOptionCategoriesLoading && (
        <Flex w="80%" direction="row">
          <Flex direction="column" w="70%">
            <Text fontWeight="semibold" fontSize="20px" mt="16px">
              Current option categories
            </Text>
            <Flex wrap="wrap" mt={4}>
              {internalOptionCategories?.option_categories.map(
                (internalOptionCategory: InternalOptionCategory, index: number) => (
                  <Box
                    ml={index > 0 ? "5px" : ""}
                    key={internalOptionCategory.id}
                    border={
                      internalOptionCategory.id === selectedInternalOptionCategory?.id
                        ? "1px solid white"
                        : "1px solid gray"
                    }
                    p={2}
                    onClick={() => handleInternalOptionCategoryClick(internalOptionCategory)}
                  >
                    <Text color="white">{internalOptionCategory.name}</Text>
                  </Box>
                )
              )}
            </Flex>
            {selectedInternalOptionCategory && (
              <Modal
                isOpen={internalOptionCategoryIsOpen}
                onClose={() => {
                  internalOptionCategoryOnClose();
                  setSelectedInternalOptionCategory(null);
                }}
              >
                <ModalOverlay />
                <ModalContent color="black" bgColor="white">
                  <ModalHeader>{`Option category: ${selectedInternalOptionCategory.name}`}</ModalHeader>
                  <ModalCloseButton />
                  <ModalBody>
                    <Text>{`ID: ${selectedInternalOptionCategory.id}`}</Text>
                    <Text>{`Name: ${selectedInternalOptionCategory.name}`}</Text>
                    <Flex direction="row" alignItems="center">
                      <Text mr={2}>Order (Number):</Text>
                      <Editable
                        value={updateOptionCategoryOrder}
                        onChange={(value) => setUpdateOptionCategoryOrder(value)}
                        w="100%"
                      >
                        <EditablePreview width="full" minH="20px" border="1px" />
                        <EditableInput />
                      </Editable>
                    </Flex>
                    <Checkbox
                      isChecked={updateOptionCategoryIsQuickMode}
                      onChange={(e) => setUpdateOptionCategoryIsQuickMode(e.target.checked)}
                    >
                      Quick Mode
                    </Checkbox>
                  </ModalBody>
                  <ModalFooter>
                    <Button
                      colorScheme="green"
                      mr={3}
                      onClick={() => handleInternalOptionCategoryUpdate()}
                    >
                      Update
                    </Button>
                    <Button
                      colorScheme="red"
                      mr={3}
                      onClick={() => handleInternalOptionCategoryDelete(false)}
                    >
                      Delete
                    </Button>
                    <Button
                      colorScheme="red"
                      mr={3}
                      onClick={() => handleInternalOptionCategoryDelete(true)}
                    >
                      Force Delete
                    </Button>
                    <Button
                      colorScheme="blue"
                      mr={3}
                      onClick={() => {
                        internalOptionCategoryOnClose();
                        setSelectedInternalOptionCategory(null);
                      }}
                    >
                      Close
                    </Button>
                  </ModalFooter>
                </ModalContent>
              </Modal>
            )}
          </Flex>
          <Flex direction="column" w="30%">
            <Text fontWeight="semibold" fontSize="20px" mt="16px">
              Create option category
            </Text>
            <VStack spacing="4" align="stretch" mt="5">
              <Input
                placeholder="Name"
                value={createOptionCategoryName}
                onChange={(e) => setCreateOptionCategoryName(e.target.value)}
              />
              <Input
                placeholder="Order (Number)"
                value={createOptionCategoryOrder}
                onChange={(e) => setCreateOptionCategoryOrder(e.target.value)}
              />
              <Checkbox
                isChecked={createOptionCategoryIsQuickMode}
                onChange={(e) => setCreateOptionCategoryIsQuickMode(e.target.checked)}
              >
                Quick Mode
              </Checkbox>
              <Button colorScheme="blue" onClick={() => handleInternalOptionCategoryCreate()}>
                Create
              </Button>
            </VStack>
          </Flex>
        </Flex>
      )}
    </Flex>
  );
};

export default OptionCategoryPanel;
